import { useEffect, useMemo, useRef } from "react";

import { ArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/arcade-instructions-modal.component";
import { useArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/use-arcade-instructions-modal";
import { ArcadeShield } from "../shared/components/arcade-shield/arcade-shield.component";
import { useRewardedVideoAd } from "../shared/hooks/use-rewarded-video-ad";
import { ArcadeGames } from "../shared/types";
import {
  getArcadeGameImageUrl,
  retrieveFromSessionStorage,
  saveInSessionStorage,
} from "../shared/utils";

import { WordSearchBanner } from "./components/banner/word-search-banner.component";
import { WordSearchProductList } from "./components/product-list/word-search-product-list.component";
import {
  WORD_SEARCH_POINT_PER_WORD,
  WORD_SEARCH_ROUND_LIMIT_LABEL,
} from "./word-search.constants";
import { useWordSearchState } from "./word-search.state";
import { WordSearchGameStatus } from "./word-search.types";

function WordSearch() {
  const {
    round,
    gameState,
    handleClick,
    handleTimeout,
    handleCplResponse,
    wordsFound,
    allTimeEntries,
  } = useWordSearchState();
  const [
    showInstructionsModal,
    handleHideInstructionsModal,
  ] = useArcadeInstructionsModal(ArcadeGames.WordSearch);
  const gameHasEnded = [
    WordSearchGameStatus.WON,
    WordSearchGameStatus.LOST,
  ].includes(gameState.status);

  const advanceRound = () => handleCplResponse(false);

  const { triggerAd } = useRewardedVideoAd({
    onAdGranted: advanceRound,
    onAdDismissed: advanceRound,
    onAdFailed: advanceRound,
  });

  const hasTriggeredAd = useRef(false);
  useEffect(() => {
    if (gameHasEnded && !hasTriggeredAd.current) {
      hasTriggeredAd.current = true;
      setTimeout(() => triggerAd(), 3000);
    }
  }, [gameHasEnded, triggerAd]);

  const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
  const redeemedEntries = Number(submission?.data.redeemedEntries) || 0;
  const arcadeAvailableGames = window.FKL.arcadeAvailableGamesInfo;

  const sessionEntries = useMemo(() => {
    const SWEEPSTAKE_SESSION_ENTRIES = "sweepstakeSessionEntries";
    const sweepstakeSessionEntries = retrieveFromSessionStorage(
      SWEEPSTAKE_SESSION_ENTRIES
    );
    if (!sweepstakeSessionEntries) {
      saveInSessionStorage(SWEEPSTAKE_SESSION_ENTRIES, 0);
      return 0;
    }
    if (wordsFound > 0) {
      saveInSessionStorage(
        SWEEPSTAKE_SESSION_ENTRIES,
        Number(sweepstakeSessionEntries) + 1
      );
      return Number(sweepstakeSessionEntries) + 1;
    }
    return Number(sweepstakeSessionEntries);
  }, [wordsFound]);

  return (
    <>
      <ArcadeShield
        className="arcade__word-search"
        roundIndex={gameState.roundIndex || 0}
        header={{
          redeemedEntries,
          sessionEntries,
          allTimeEntries,
          extraEntries: wordsFound,
        }}
      >
        <WordSearchBanner
          round={round}
          words={gameState.words}
          wordsFound={wordsFound}
          status={gameState.status}
          onTimeout={handleTimeout}
        />
        <div className="word-search__canvas">
          <WordSearchProductList round={round} words={gameState.words} />
          <div className="word-search">
            {gameState.fields.map((field) => (
              <div
                key={field.index}
                onClick={() => handleClick(field)}
                role="button"
                tabIndex={field.index}
                className={`word-search-item status-${field.status.toLowerCase()}`}
              >
                {field.value}
              </div>
            ))}
          </div>
        </div>
      </ArcadeShield>
      <ArcadeInstructionsModal
        isOpen={showInstructionsModal}
        title="Word Search"
        image={getArcadeGameImageUrl(arcadeAvailableGames, document.location.href)}
        instructions={
          <>
            <ul className="modal-instructions">
              <li>
                Find
                <strong>
                  {" "}
                  {round && round.products.length} product names
                </strong>{" "}
                in {WORD_SEARCH_ROUND_LIMIT_LABEL}.
              </li>
              <li>
                Click the first and last letter of a word you found to quickly
                select it.
              </li>
              <li>
                Win <strong>+{WORD_SEARCH_POINT_PER_WORD} point</strong> for each
                word you find!
              </li>
            </ul>
          </>
        }
        onHideInstructionsModal={handleHideInstructionsModal}
      />
    </>
  );
}

export const Widget = WordSearch;
